import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, PackageX } from "lucide-react";
import { PublicLayout, EmptyState } from "../components/Common";
import { ProductCard, ProductCardSkeleton } from "../components/ProductCard";
import { PRODUCT_TYPES } from "../lib/format";
import api from "../lib/api";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [page, setPage] = useState(1);

  const category_id = params.get("category_id") || "";
  const type = params.get("type") || "";
  const sort = params.get("sort") || "newest";
  const is_free = params.get("is_free") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  const fetchProducts = useCallback(() => {
    setData(null);
    const sp = new URLSearchParams();
    if (params.get("q")) sp.set("q", params.get("q"));
    if (category_id) sp.set("category_id", category_id);
    if (type) sp.set("product_type", type);
    if (sort) sp.set("sort", sort);
    if (is_free === "1") sp.set("is_free", "true");
    if (is_free === "0") sp.set("is_free", "false");
    sp.set("page", page);
    sp.set("limit", "12");
    api.get(`/products?${sp.toString()}`).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0, pages: 0 }));
  }, [params, category_id, type, sort, is_free, page]);

  useEffect(() => {
    document.title = "Katalog Produk - DS KODE";
    fetchProducts();
  }, [fetchProducts]);

  const update = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val);
    else next.delete(key);
    setParams(next);
    setPage(1);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    update("q", q);
  };

  return (
    <PublicLayout>
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">Katalog Produk</h1>
          <p className="text-slate-500 mt-1">Temukan aplikasi, source code, template & script berkualitas.</p>
          <form onSubmit={submitSearch} className="mt-5 relative max-w-xl">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input data-testid="catalog-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari aplikasi atau source code..." className="w-full pl-11 pr-24 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            <button type="submit" className="absolute right-1.5 top-1/2 -translate-y-1/2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold">Cari</button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters */}
        <aside className="lg:col-span-1 space-y-5">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-4"><SlidersHorizontal className="h-4 w-4 text-indigo-600" /><span className="font-heading font-semibold text-slate-900">Filter</span></div>

            <FilterGroup label="Urutkan">
              <select data-testid="filter-sort" value={sort} onChange={(e) => update("sort", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm">
                <option value="newest">Terbaru</option>
                <option value="popular">Terpopuler</option>
                <option value="price_low">Harga Termurah</option>
                <option value="price_high">Harga Tertinggi</option>
              </select>
            </FilterGroup>

            <FilterGroup label="Jenis Produk">
              <select data-testid="filter-type" value={type} onChange={(e) => update("type", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm">
                <option value="">Semua Jenis</option>
                {PRODUCT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </FilterGroup>

            <FilterGroup label="Kategori">
              <select data-testid="filter-category" value={category_id} onChange={(e) => update("category_id", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm">
                <option value="">Semua Kategori</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </FilterGroup>

            <FilterGroup label="Harga">
              <select data-testid="filter-price" value={is_free} onChange={(e) => update("is_free", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm">
                <option value="">Semua</option>
                <option value="1">Gratis</option>
                <option value="0">Berbayar</option>
              </select>
            </FilterGroup>

            <button onClick={() => { setParams({}); setQ(""); setPage(1); }} className="w-full mt-2 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Reset Filter</button>
          </div>
        </aside>

        {/* Grid */}
        <div className="lg:col-span-3">
          {data && <p className="text-sm text-slate-500 mb-4" data-testid="catalog-count">Menampilkan {data.total} produk</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {data === null
              ? Array.from({ length: 6 }).map((_, i) => <ProductCardSkeleton key={i} />)
              : data.items.map((p) => <ProductCard key={p.id} p={p} />)}
          </div>

          {data && data.items.length === 0 && <EmptyState icon={PackageX} title="Belum ada produk tersedia." subtitle="Coba ubah kata kunci atau filter pencarian." />}

          {data && data.pages > 1 && (
            <div className="flex justify-center gap-2 mt-8" data-testid="pagination">
              {Array.from({ length: data.pages }).map((_, i) => (
                <button key={i} onClick={() => setPage(i + 1)} className={`h-9 w-9 rounded-lg text-sm font-semibold transition-colors ${page === i + 1 ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div className="mb-4">
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>
      {children}
    </div>
  );
}
